/**
 * FieldCell
 *
 * A hoverable/clickable cell for displaying an extracted field value.
 * Supports hover highlighting and click-to-lock behavior.
 */

import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type {
  ConfidenceScores,
  Citation,
} from '@/components/document-comparison/types';
import { ConfidenceIndicator } from './ConfidenceBadge';

interface FieldCellProps {
  /** The field path (e.g., "line_items[0].quantity") */
  path: string;
  /** The displayed value */
  value: ReactNode;
  /** Whether this field is currently hovered */
  isHovered: boolean;
  /** Whether this field is locked (persistent highlight) */
  isLocked: boolean;
  /** Confidence scores for this field */
  confidence?: ConfidenceScores;
  /** Whether this field has citations */
  hasCitation: boolean;
  /** Page number(s) where the citation appears */
  citationPages?: number[];
  /** Reference text from the citation */
  referenceText?: string;
  /** Callback when hovering */
  onHover: (hovering: boolean) => void;
  /** Callback when clicking */
  onClick: () => void;
  /** Additional CSS classes */
  className?: string;
}

export function FieldCell({
  path,
  value,
  isHovered,
  isLocked,
  confidence,
  hasCitation,
  citationPages,
  referenceText,
  onHover,
  onClick,
  className,
}: FieldCellProps) {
  const isHighlighted = isHovered || isLocked;
  const showPageBadge = citationPages && citationPages.length > 0;

  return (
    <div
      className={cn(
        'relative px-2 py-1.5 rounded-sm transition-colors cursor-pointer',
        hasCitation && 'hover:bg-primary/10',
        isHighlighted && hasCitation && 'bg-primary/15 ring-1 ring-primary/30',
        isLocked && hasCitation && 'bg-primary/20 ring-1 ring-primary/50',
        !hasCitation && 'cursor-default opacity-80',
        className,
      )}
      onMouseEnter={() => hasCitation && onHover(true)}
      onMouseLeave={() => hasCitation && onHover(false)}
      onClick={() => hasCitation && onClick()}
    >
      <div className="flex items-center gap-1.5">
        {/* Value */}
        <span className="flex-1 truncate">{value}</span>

        {/* Confidence indicator */}
        {confidence && <ConfidenceIndicator scores={confidence} />}

        {/* Page badge */}
        {showPageBadge && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                p.{citationPages.join(',')}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">
                Found on page {citationPages.join(', ')}
              </p>
              {referenceText && (
                <p className="text-xs text-muted-foreground mt-1 max-w-xs truncate">
                  "{referenceText}"
                </p>
              )}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

/**
 * A simpler version of FieldCell for use in table cells.
 */
interface TableFieldCellProps {
  path: string;
  value: ReactNode;
  isHovered: boolean;
  isLocked: boolean;
  hasCitation: boolean;
  onHover: (hovering: boolean) => void;
  onClick: () => void;
  className?: string;
}

export function TableFieldCell({
  path,
  value,
  isHovered,
  isLocked,
  hasCitation,
  onHover,
  onClick,
  className,
}: TableFieldCellProps) {
  const isHighlighted = isHovered || isLocked;

  return (
    <div
      className={cn(
        'px-1 py-0.5 -mx-1 rounded-sm transition-colors',
        hasCitation && 'cursor-pointer hover:bg-primary/10',
        isHighlighted && hasCitation && 'bg-primary/15',
        isLocked && hasCitation && 'bg-primary/20',
        !hasCitation && 'cursor-default',
        className,
      )}
      onMouseEnter={() => hasCitation && onHover(true)}
      onMouseLeave={() => hasCitation && onHover(false)}
      onClick={(e) => {
        if (hasCitation) {
          e.stopPropagation();
          onClick();
        }
      }}
    >
      {value}
    </div>
  );
}

/**
 * Helper component for labeled fields (label + value pair).
 */
interface LabeledFieldProps {
  label: string;
  path: string;
  value: ReactNode;
  isHovered: boolean;
  isLocked: boolean;
  confidence?: ConfidenceScores;
  citations: Citation[];
  onHover: (path: string | null) => void;
  onClick: (path: string) => void;
}

export function LabeledField({
  label,
  path,
  value,
  isHovered,
  isLocked,
  confidence,
  citations,
  onHover,
  onClick,
}: LabeledFieldProps) {
  const hasCitation = citations.length > 0;
  const citationPages = [...new Set(citations.map((c) => c.page))];
  const referenceText = citations[0]?.referenceText;

  return (
    <div className="space-y-0.5">
      <label className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <FieldCell
        path={path}
        value={value ?? <span className="text-muted-foreground italic">—</span>}
        isHovered={isHovered}
        isLocked={isLocked}
        confidence={confidence}
        hasCitation={hasCitation}
        citationPages={citationPages}
        referenceText={referenceText}
        onHover={(hovering) => onHover(hovering ? path : null)}
        onClick={() => onClick(path)}
      />
    </div>
  );
}
